import { GeoBadge } from "@/components/auth/GeoBadge";
import { VpnWarningBanner } from "@/components/auth/VpnWarningBanner";
import { BetaComplianceBanner } from "@/components/dashboard/BetaComplianceBanner";
import LiquidGlassCard from "@/components/landing/LiquidGlassCard";
import { LandingFooter } from "@/components/public/LandingFooter";
import { PublicHeader } from "@/components/public/PublicHeader";
import { ListSkeleton } from "@/components/skeletons/SkeletonLayouts";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { InlineLoadingIndicator } from "@/components/ui/inline-loading";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SFIcon } from '@/components/ui/sf-icon';
import { Heading, Text } from "@/components/ui/typography";
import { getDomain } from '@/config/domains';
import { toast } from "@/hooks/use-toast";
import { useAuthPreflight } from "@/hooks/useAuthPreflight";
import { useGeoInBackground } from "@/hooks/useGeoInBackground";
import { useOnboardingStatus } from "@/hooks/useOnboardingStatus";
import { useSession } from "@/hooks/useSession";
import { useTelegramAuth } from "@/hooks/useTelegramAuth";
import { trackUX, UX_EVENTS } from '@/lib/analytics/ux-events';
import { isAdminDomain } from '@/lib/domains/admin';
import { signOut } from '@/lib/rpc/auth-facade';
import { sfIcons } from '@/lib/sf-icons';
import { TELEGRAM_CONFIG } from "@/lib/telegramConfig";
import { cn } from '@/lib/utils';
import { getErrorMessage } from "@/lib/utils/error-display";
import { semanticTokens } from '@packages/config/theme';
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

function detectDeviceType() {
  if (typeof navigator === 'undefined') {
    return 'unknown';
  }

  const ua = navigator.userAgent || '';
  if (/Telegram/i.test(ua)) {
    return 'telegram';
  }
  if (/Mobi|Android/i.test(ua)) {
    return 'mobile';
  }
  return 'desktop';
}

const AuthPage = () => {
  const { t } = useTranslation("common");
  const { status, user } = useSession();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [banner, setBanner] = useState<{ kind: 'info' | 'warning'; text: string } | null>(null);
  const [requestId, setRequestId] = useState<string | null>(null);
  const [retryLeft, setRetryLeft] = useState<number | null>(null);
  const isAdminHost = typeof window !== 'undefined' ? isAdminDomain() : false;
  // Surface is determined automatically based on domain
  const surface: 'public' | 'admin' = isAdminHost ? 'admin' : 'public';

  const linkingRef = useRef(false);

  const { isInTelegram, isAuthenticated, loading: tgLoading, user: tgUser } = useTelegramAuth();
  const { data: preflight } = useAuthPreflight(email);
  const { startOnboarding, pollStatus, isPolling, redirectProductIfVerified } = useOnboardingStatus();

  // Geographic detection in background (non-blocking)
  const {
    isDetecting: geoDetecting,
    country,
    city,
    restrictions,
    vpnDetected,
    confidence
  } = useGeoInBackground();

  // RequireGuest wrapper already handles authentication redirects

  // For Telegram Mini App, show different UI
  const isNativeTelegramAuth = isInTelegram && isAuthenticated;

  // Countdown timer for rate limiting
  useEffect(() => {
    if (!preflight?.rate_limited || !preflight?.retry_after_ms) {
      setRetryLeft(null);
      return;
    }

    const endTime = Date.now() + preflight.retry_after_ms;
    setRetryLeft(Math.ceil(preflight.retry_after_ms / 1000));

    const interval = setInterval(() => {
      const timeLeft = Math.max(0, Math.ceil((endTime - Date.now()) / 1000));
      setRetryLeft(timeLeft);
      if (timeLeft === 0) {
        clearInterval(interval);
        setRetryLeft(null);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [preflight?.rate_limited, preflight?.retry_after_ms]);

  const handleSendMagicLink = async () => {
    if (!email) return;

    trackUX(UX_EVENTS.AUTH_MAGIC_LINK_REQUESTED, {
      surface,
      device_type: detectDeviceType(),
    });

    setLoading(true);

    // Clear any existing session first
    const { error: signOutError } = await signOut();

    // Use proper Edge Function for magic link via RPC-first approach
    const redirectSurface = surface;

    // Check if there's a redirect_to param (for cross-domain auth like Atlas)
    const urlParams = new URLSearchParams(window.location.search);
    const externalRedirect = urlParams.get('redirect_to');

    // Build callback URL with external redirect if present
    let redirectUrl = `${getDomain(redirectSurface)}/auth/callback`;
    if (externalRedirect) {
      redirectUrl += `?redirect_to=${encodeURIComponent(externalRedirect)}`;
    }

    let error: any = null;

    try {
      // P0 FIX: RPC-first approach - no direct supabase in UI
      const { sendMagicLink } = await import('@/lib/rpc/auth-facade');
      const result = await sendMagicLink({
        email,
        redirect_to: redirectUrl,
        create_user: redirectSurface === 'public',
        surface: redirectSurface
      });

      if (!result.success) {
        error = result.error;
      }

      setRequestId(result.data?.request_id || null);
    } catch (fetchError) {
      error = fetchError;
    }

    if (error) {
      setLoading(false);

      // Track error event
      const errorMessage = getErrorMessage(error);
      const isRateLimit = errorMessage.toLowerCase().includes('rate') ||
        errorMessage.toLowerCase().includes('limit') ||
        preflight?.rate_limited;

      if (isRateLimit) {
        trackUX(UX_EVENTS.AUTH_MAGIC_LINK_RATE_LIMITED, {
          surface,
          device_type: detectDeviceType(),
          retry_after_ms: preflight?.retry_after_ms,
        });
      } else {
        trackUX(UX_EVENTS.AUTH_MAGIC_LINK_FAILED, {
          surface,
          device_type: detectDeviceType(),
          error_type: error instanceof Error ? error.constructor.name : 'unknown',
        });
      }

      toast({ title: t("auth.error"), description: errorMessage, variant: "destructive" });
      return;
    }

    // Dynamic message based on preflight recommendation
    toast({
      title: t("auth.checkInbox"),
      description: t("auth.checkInboxDescription")
    });

    setLoading(false);
  };

  // Show loading for Telegram initialization
  if (isInTelegram && tgLoading) {
    return (
      <div className="min-h-svh flex items-center justify-center px-4">
        <LiquidGlassCard
          tier="surface"
          className="w-full max-w-md"
        >
          <div className="p-6">
            <div role="status" aria-live="polite" className="space-y-4 py-4 text-center">
              <ListSkeleton items={4} />
              <Text variant="sm" className={semanticTokens.fg.muted}>{t("auth.connectingTelegram")}</Text>
            </div>
          </div>
        </LiquidGlassCard>
      </div>
    );
  }

  // Determine if VPN/restriction banner should show
  const showVpnBanner = vpnDetected || restrictions?.has_restrictions;

  // Handler for coverage link
  const handleViewCoverage = () => {
    window.open('https://supabank.xyz/coverage', '_blank');
  };

  return (
    <main id="main" className="min-h-screen flex flex-col">
      <PublicHeader />

      {/* Beta Launch Banner - shown only on production */}
      <div className="container mx-auto px-4 pt-4">
        <BetaComplianceBanner />
      </div>

      {/* Spacer between header and form */}
      <div className="flex-1 flex items-center justify-center px-4 py-12 lg:py-16">
        <LiquidGlassCard
          tier="surface"
          className="w-full max-w-md"
        >
          <div className="p-6 space-y-6">
            <div className="space-y-3">
              <Heading level={2} className="text-center">{t("auth.welcomeToSupabank")}</Heading>

              {/* Geographic location indicator - subtle, non-blocking */}
              <GeoBadge
                isDetecting={geoDetecting}
                country={country}
                city={city}
                vpnDetected={vpnDetected}
                hasRestrictions={restrictions?.has_restrictions || false}
                confidence={confidence}
                className="mt-1"
              />

              {/* Telegram Status Badge */}
              {isInTelegram && tgUser && (
                <Badge variant="outline" className={cn(semanticTokens.info.bg, semanticTokens.info.fg, semanticTokens.info.border, "w-fit mt-2")}>
                  <SFIcon icon={sfIcons.user} className="w-4 h-4 mr-2" />
                  {tgUser.first_name} via Telegram
                </Badge>
              )}
            </div>

            <div className="space-y-6">
              {/* VPN/Restriction warning banner (if needed) */}
              {showVpnBanner && (
                <VpnWarningBanner
                  show={showVpnBanner}
                  vpnDetected={vpnDetected}
                  hasRestrictions={restrictions?.has_restrictions || false}
                  restrictionMessage={restrictions?.message}
                  onViewCoverage={handleViewCoverage}
                />
              )}

              {/* Status banner for onboarding messages */}
              {banner && (
                <div className={cn(
                  "p-4 rounded-lg border flex items-center gap-3",
                  banner.kind === 'info'
                    ? cn(semanticTokens.info.bg, semanticTokens.info.border, semanticTokens.info.fg)
                    : cn(semanticTokens.warning.bg, semanticTokens.warning.border, semanticTokens.warning.fg)
                )}>
                  <SFIcon icon={sfIcons.info} className="w-5 h-5 shrink-0" />
                  <Text variant="sm">{banner.text}</Text>
                  {isPolling && (
                    <InlineLoadingIndicator
                      size="sm"
                      className="text-current shrink-0"
                      aria-label="Checking onboarding status"
                    />
                  )}
                </div>
              )}

              {/* Show different content for Telegram vs Web */}
              {isInTelegram ? (
                // Telegram users: Direct them to Mini App
                <div className="space-y-4 text-center">
                  <div className={cn("p-4 border rounded-lg", semanticTokens.info.bg, semanticTokens.info.border)}>
                    <SFIcon icon={sfIcons.user} className={cn("w-8 h-8 mx-auto mb-2", semanticTokens.info.fg)} />
                    <Text variant="sm" className={cn(semanticTokens.info.fg, "font-medium")}>
                      {t("auth.telegramOptimized")}
                    </Text>
                  </div>

                  <Button
                    className="w-full min-h-[48px] text-base sm:text-sm touch-manipulation"
                    size="lg"
                    asChild
                  >
                    <a href={TELEGRAM_CONFIG.MINI_APP_URL} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
                      <SFIcon icon={sfIcons.externalLink} className="w-4 h-4" />
                      {t("auth.openMiniApp")}
                    </a>
                  </Button>

                  <Text variant="caption" className={semanticTokens.fg.muted}>
                    {t("auth.orContinueOnWeb")}
                  </Text>
                </div>
              ) : null}

              {/* Email form - always show for web, optional for Telegram */}
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="email" className={semanticTokens.typography.label}>{t("auth.emailAddress")}</Label>
                </div>
                <Input
                  id="email"
                  type="email"
                  placeholder={t("auth.emailPlaceholder")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="min-h-[44px] text-base sm:text-sm touch-manipulation"
                  autoComplete="email"
                  inputMode="email"
                />

                {preflight?.rate_limited && retryLeft !== null && retryLeft > 0 && (
                  <Text variant="caption" className={cn(semanticTokens.warning.fg, "mb-2")}>
                    {t("auth.tryAgainIn", { seconds: retryLeft })}
                  </Text>
                )}

                {preflight?.rate_limited && (retryLeft === null || retryLeft === 0) && (
                  <Text variant="caption" className={cn(semanticTokens.warning.fg, "mb-2")}>
                    {t("auth.tooManyAttempts")}
                  </Text>
                )}


                <Button
                  className="w-full min-h-[48px] text-base sm:text-sm touch-manipulation"
                  onClick={handleSendMagicLink}
                  disabled={loading || !/\S+@\S+\.\S+/.test(email) || (preflight?.rate_limited && retryLeft !== null && retryLeft > 0)}
                  size="lg"
                  data-testid="auth-continue"
                >
                  {loading ? t("auth.sendingMagicLink") :
                    (preflight?.rate_limited && retryLeft !== null && retryLeft > 0) ? t("auth.rateLimitedWait") :
                      t("auth.continueWithEmail")}
                </Button>
              </div>
            </div>
          </div>
        </LiquidGlassCard>
      </div>

      {/* Spacer between form and footer */}
      <div className="h-12 lg:h-16" aria-hidden="true" />

      {/* Footer at the bottom */}
      <LandingFooter />
    </main>
  );
};

export default AuthPage;
